import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams, PaginatedMemberReportList } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseNewMembers {
  dateStart?: string;
  dateEnd?: string;
  ordering?: string;
  enabled?: boolean;
  csvExport?: string;
  limit?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useNewMembers({
  dateStart,
  dateEnd,
  ordering,
  enabled = true,
  csvExport,
  limit = '20',
}: IUseNewMembers): UseInfiniteQueryResult<AxiosResponse<PaginatedMemberReportList>> {
  const fetchNewMembers = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedMemberReportList>> => {
      const params = pageParam;

      if (dateStart !== undefined) {
        params.start_date = dateStart;
      }
      if (dateEnd !== undefined) {
        params.end_date = dateEnd;
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`).concat(',pk');
      }

      return client.get(`reports/new-members?${encodeUrlParams(params)}`);
    },
    [limit, ordering, dateStart, dateEnd],
  );

  return useInfiniteQuery(['new-members', dateStart, dateEnd, ordering, csvExport], fetchNewMembers, {
    getNextPageParam: page => {
      return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
    },
    enabled: enabled && dateStart !== undefined && dateEnd !== undefined,
    staleTime: 86400000, // 1 day
  });
}

export default useNewMembers;
